import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Volume2, Square, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const LABEL = { en: ["Listen", "Stop"], hi: ["सुनें", "रोकें"], or: ["ଶୁଣନ୍ତୁ", "ବନ୍ଦ କରନ୍ତୁ"] };
const blobs = new Map();
let ctx, wetBus;

// Shared Web Audio graph: dry signal + soft room reverb (generated impulse)
function graph() {
  if (ctx) return ctx;
  ctx = new (window.AudioContext || window.webkitAudioContext)();
  const len = Math.floor(ctx.sampleRate * 1.6);
  const ir = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = ir.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2);
  }
  const conv = ctx.createConvolver();
  conv.buffer = ir;
  const wet = ctx.createGain();
  wet.gain.value = 0.16;
  conv.connect(wet).connect(ctx.destination);
  wetBus = conv;
  return ctx;
}

let current = null;

export function ListenButton({ text, lang = "en", testId = "listen-btn", compact = false, className = "" }) {
  const [state, setState] = useState("idle");
  const el = useRef(null);
  const stop = () => { el.current?.pause(); if (el.current) el.current.currentTime = 0; setState("idle"); };
  useEffect(() => () => { el.current?.pause(); }, []);
  useEffect(() => { stop(); }, [text, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = async () => {
    if (state === "playing") return stop();
    current?.();
    setState("loading");
    try {
      const key = `${lang}:${text}`;
      if (!blobs.has(key)) {
        const { data } = await api.post("/tts", { text, lang }, { responseType: "blob" });
        blobs.set(key, URL.createObjectURL(data));
      }
      const ac = graph();
      if (ac.state === "suspended") await ac.resume();
      if (!el.current) {
        el.current = new Audio();
        const src = ac.createMediaElementSource(el.current);
        src.connect(ac.destination);
        src.connect(wetBus);
        el.current.onended = () => setState("idle");
      }
      el.current.src = blobs.get(key);
      await el.current.play();
      current = stop;
      setState("playing");
    } catch (e) {
      setState("idle");
      let msg = errMsg(e);
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep */ } }
      toast.error(msg);
    }
  };

  const [on, off] = LABEL[lang] || LABEL.en;
  const Icon = state === "loading" ? Loader2 : state === "playing" ? Square : Volume2;
  return (
    <button type="button" onClick={play} disabled={!text || state === "loading"} aria-label={state === "playing" ? off : on} data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border border-[#2412B5]/25 text-[#2412B5] hover:bg-[#2412B5]/5 transition-colors disabled:opacity-60 ${compact ? "p-1" : "px-3 py-1 text-xs font-medium"} ${className}`}>
      <Icon className={`${compact ? "h-3.5 w-3.5" : "h-3.5 w-3.5"} ${state === "loading" ? "animate-spin" : ""}`} />
      {!compact && (state === "playing" ? off : on)}
      {state === "playing" && !compact && <span className="flex items-end gap-[2px] h-3" aria-hidden="true">{[0, 1, 2].map((i) => <span key={i} className="w-[2px] bg-current rounded eq-bar" style={{ animationDelay: `${i * 0.15}s` }} />)}</span>}
    </button>
  );
}
